import { Link, useParams } from "@tanstack/solid-router";
import { For, Show, createEffect, createSignal } from "solid-js";

import { AppShell } from "~/components/AppShell";
import { CustomFieldsRenderer } from "~/components/CustomFieldsRenderer";
import { CustomRecordsCsvButtons } from "~/components/CustomRecordsCsvButtons";
import { SavedViewsBar } from "~/components/SavedViewsBar";
import {
  useArchiveCustomRecord,
  useCreateCustomRecord,
  useCustomObjects,
  useCustomRecord,
  useCustomRecords,
  useUpdateCustomRecord,
} from "~/lib/hooks";
import type {
  CustomObjectDefinitionRead,
  CustomRecordCreate,
  CustomRecordUpdate,
} from "~/lib/types";

type EditState = { mode: "add" } | { mode: "edit"; recordId: string };

type FormState = {
  name: string;
  data: Record<string, unknown>;
};

const blankForm = (): FormState => ({ name: "", data: {} });

export function CustomRecordsPage() {
  const params = useParams({ strict: false }) as () => {
    wsKey: string;
    objectKey: string;
  };

  // Resolve the object definition by URL key so the page works regardless
  // of cod_id stability. Load *all* (including archived) so the URL keeps
  // working if an admin archives the object — the page just shows a
  // friendly "archived" state instead of 404ing.
  const objects = useCustomObjects(
    () => params().wsKey,
    () => ({ include_archived: true }),
  );
  const matched = (): CustomObjectDefinitionRead | undefined =>
    objects.data?.items.find((o) => o.key === params().objectKey);

  return (
    <Show
      when={objects.data}
      fallback={
        <AppShell wsKey={params().wsKey} pageTitle="Custom records">
          <div class="py-12 text-center opacity-60">Loading…</div>
        </AppShell>
      }
    >
      <Show
        when={matched()}
        fallback={
          <AppShell wsKey={params().wsKey} pageTitle="Custom records">
            <div class="py-12 text-center">
              <p class="text-lg font-semibold">No object named "{params().objectKey}"</p>
              <p class="text-sm opacity-60 mt-2">Declare one in Settings → Custom objects.</p>
            </div>
          </AppShell>
        }
      >
        {(obj) => <Body wsKey={() => params().wsKey} obj={obj()} />}
      </Show>
    </Show>
  );
}

function Body(props: { wsKey: () => string; obj: CustomObjectDefinitionRead }) {
  const [search, setSearch] = createSignal("");
  const [tagFilter, setTagFilter] = createSignal<string>("");
  const [includeArchived, setIncludeArchived] = createSignal(false);
  const [edit, setEdit] = createSignal<EditState | null>(null);

  const records = useCustomRecords(
    props.wsKey,
    () => props.obj.id,
    () => ({
      q: search() || undefined,
      tag: tagFilter() || undefined,
      include_archived: includeArchived() || undefined,
    }),
  );
  const archiveMutation = useArchiveCustomRecord(props.wsKey, () => props.obj.id);

  return (
    <AppShell wsKey={props.wsKey()} pageTitle={props.obj.label_plural}>
      <div class="flex flex-col gap-4">
        <Show when={props.obj.archived_at}>
          <div role="alert" class="alert alert-warning text-sm">
            This object is archived. Existing records remain readable; create and edit are still
            allowed.
          </div>
        </Show>

        <SavedViewsBar
          wsKey={props.wsKey}
          entityType={() => "custom_record"}
          customObjectDefinitionId={() => props.obj.id}
          currentFilters={() => ({
            q: search() || undefined,
            tag: tagFilter() || undefined,
            include_archived: includeArchived() || undefined,
          })}
          onApply={(f) => {
            setSearch((f.q as string) ?? "");
            setTagFilter((f.tag as string) ?? "");
            setIncludeArchived(Boolean(f.include_archived));
          }}
          onClear={() => {
            setSearch("");
            setTagFilter("");
            setIncludeArchived(false);
          }}
        />

        <div class="flex items-center gap-3 flex-wrap">
          <input
            type="search"
            class="input input-bordered input-sm w-72"
            placeholder={`Search ${props.obj.label_plural.toLowerCase()}…`}
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
            aria-label={`Search ${props.obj.label_plural.toLowerCase()}`}
          />
          <input
            type="text"
            class="input input-bordered input-sm w-40"
            placeholder="Filter by tag…"
            value={tagFilter()}
            onInput={(e) => setTagFilter(e.currentTarget.value.trim())}
            aria-label="Filter by tag"
          />
          <label class="label cursor-pointer gap-2">
            <input
              type="checkbox"
              class="checkbox checkbox-sm"
              checked={includeArchived()}
              onChange={(e) => setIncludeArchived(e.currentTarget.checked)}
            />
            <span class="label-text">Include archived</span>
          </label>
          <div class="flex-1" />
          <CustomRecordsCsvButtons
            wsKey={props.wsKey}
            codId={() => props.obj.id}
            objectKey={() => props.obj.key}
            objectLabelPlural={() => props.obj.label_plural}
          />
          <button
            type="button"
            class="btn btn-primary btn-sm"
            onClick={() => setEdit({ mode: "add" })}
          >
            Add {props.obj.label_singular.toLowerCase()}
          </button>
        </div>

        <div class="card bg-base-100 shadow-sm">
          <div class="card-body p-0">
            <div class="overflow-x-auto">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Updated</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <For each={records.data?.items ?? []}>
                    {(r) => {
                      const isArchived = r.archived_at !== null;
                      return (
                        <tr class={isArchived ? "opacity-50" : ""}>
                          <td class="font-medium">
                            <Link
                              to="/workspaces/$wsKey/custom/$objectKey/$recordId"
                              params={{
                                wsKey: props.wsKey(),
                                objectKey: props.obj.key,
                                recordId: r.id,
                              }}
                              class="link link-primary"
                            >
                              {r.display_name || r.name}
                            </Link>
                          </td>
                          <td class="text-xs opacity-60">
                            {new Date(r.updated_at).toLocaleString()}
                          </td>
                          <td class="text-right whitespace-nowrap">
                            <Show when={!isArchived}>
                              <button
                                type="button"
                                class="btn btn-ghost btn-xs"
                                onClick={() => setEdit({ mode: "edit", recordId: r.id })}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                class="btn btn-ghost btn-xs text-error"
                                onClick={() =>
                                  archiveMutation.mutate({
                                    recId: r.id,
                                    version: r.version,
                                  })
                                }
                              >
                                Archive
                              </button>
                            </Show>
                            <Show when={isArchived}>
                              <span class="badge badge-ghost badge-sm">archived</span>
                            </Show>
                          </td>
                        </tr>
                      );
                    }}
                  </For>
                  <Show when={(records.data?.items.length ?? 0) === 0 && !records.isLoading}>
                    <tr>
                      <td colspan={3} class="text-center py-6 opacity-60 text-sm">
                        No {props.obj.label_plural.toLowerCase()} yet.
                      </td>
                    </tr>
                  </Show>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <Show when={edit()}>
          {(state) => (
            <RecordModal
              wsKey={props.wsKey}
              obj={props.obj}
              state={state()}
              onClose={() => setEdit(null)}
            />
          )}
        </Show>
      </div>
    </AppShell>
  );
}

type ModalProps = {
  wsKey: () => string;
  obj: CustomObjectDefinitionRead;
  state: EditState;
  onClose: () => void;
};

function RecordModal(props: ModalProps) {
  const createMutation = useCreateCustomRecord(props.wsKey, () => props.obj.id);
  const updateMutation = useUpdateCustomRecord(props.wsKey, () => props.obj.id);
  const [form, setForm] = createSignal<FormState>(blankForm());
  const [error, setError] = createSignal<string | null>(null);
  const [hydrated, setHydrated] = createSignal(props.state.mode === "add");

  const fetched = useCustomRecord(
    props.wsKey,
    () => props.obj.id,
    () => (props.state.mode === "edit" ? props.state.recordId : null),
  );

  createEffect(() => {
    if (hydrated() || !fetched.data) return;
    const r = fetched.data;
    setForm({
      name: r.name,
      data: (r.data ?? {}) as Record<string, unknown>,
    });
    setHydrated(true);
  });

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    setError(null);
    const f = form();
    if (!f.name.trim()) return;
    const data = Object.keys(f.data).length > 0 ? f.data : null;
    try {
      if (props.state.mode === "add") {
        const payload: CustomRecordCreate = { name: f.name.trim(), data };
        await createMutation.mutateAsync(payload);
      } else if (fetched.data) {
        const payload: CustomRecordUpdate = { name: f.name.trim(), data };
        await updateMutation.mutateAsync({
          recId: fetched.data.id,
          version: fetched.data.version,
          body: payload,
        });
      }
      props.onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const title = () =>
    props.state.mode === "add"
      ? `Add ${props.obj.label_singular}`
      : `Edit ${props.obj.label_singular}`;

  return (
    <dialog open class="modal modal-open" aria-modal="true">
      <div class="modal-box max-w-2xl">
        <h3 class="font-bold text-lg mb-4">{title()}</h3>
        <Show
          when={hydrated()}
          fallback={
            <div role="status" class="text-center py-6 opacity-60">
              Loading…
            </div>
          }
        >
          <form onSubmit={handleSubmit} class="flex flex-col gap-3">
            <div>
              <label class="label">
                <span class="label-text">Name</span>
              </label>
              <input
                class="input input-bordered w-full"
                placeholder="Name"
                aria-label="Name"
                value={form().name}
                onInput={(e) => setForm((f) => ({ ...f, name: e.currentTarget.value }))}
                required
              />
            </div>

            <CustomFieldsRenderer
              wsKey={props.wsKey}
              customObjectDefinitionId={props.obj.id}
              values={() => form().data}
              onChange={(key, value) =>
                setForm((f) => {
                  const next = { ...f.data };
                  if (
                    value === null ||
                    value === "" ||
                    (Array.isArray(value) && value.length === 0)
                  ) {
                    delete next[key];
                  } else {
                    next[key] = value;
                  }
                  return { ...f, data: next };
                })
              }
            />

            <Show when={error()}>
              <div role="alert" class="alert alert-error text-sm">
                {error()}
              </div>
            </Show>

            <div class="modal-action">
              <button type="button" class="btn btn-ghost" onClick={props.onClose}>
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {props.state.mode === "add" ? "Add" : "Save"}
              </button>
            </div>
          </form>
        </Show>
      </div>
    </dialog>
  );
}
